/**
 * SIMPLIFIED PDF Translation Approach
 * 
 * Create a simple translate function that works like the original,
 * but accesses the JSON directly without React context.
 */

import globalTranslations from '../../../translations/global.json';

type LanguageCode = 'en' | 'es' | 'fr';

// Simple language index mapping
const LANG_INDEX = { en: 0, es: 1, fr: 2 };

/**
 * Simple translate function that mimics react-localize-redux
 * Usage: t('pages.hospital.request.pdf.completion_date')
 * Returns the translation key if translation is not found (to spot missing translations)
 */
export const createPDFTranslate = (language: LanguageCode = 'fr') => {
  return (key: string): string => {
    try {
      const keys = key.split('.');
      let translation: any = globalTranslations;
      
      // Navigate to the translation
      for (const k of keys) {
        translation = translation?.[k];
        if (!translation) {
          // Translation path not found, return key to spot missing translations
          return key;
        }
      }
      
      // Get the translation for the language
      if (Array.isArray(translation)) {
        const langIndex = LANG_INDEX[language];
        const result = translation[langIndex];
        return result || key;
      }
      
      return translation || key;
    } catch (error) {
      return key;
    }
  };
};

// Export a default instance for French
export const t = createPDFTranslate('fr');

// Create instances for other languages
export const tEn = createPDFTranslate('en');
export const tEs = createPDFTranslate('es');

// Or create a dynamic version with locale normalization
export const createT = (locale: string) => {
  // Normalize locale: ensure it's 'en', 'es', or 'fr'
  const normalized = locale?.toLowerCase().startsWith('en') ? 'en' : 
                     locale?.toLowerCase().startsWith('es') ? 'es' : 'fr';
  return createPDFTranslate(normalized as LanguageCode);
};
